import { z } from 'zod';

import { metricFilterSchema, metricKeySchema, type MetricKey } from './analytics.js';
import { boolParam, csvParam, sortDirSchema, type Page } from './common.js';
import { entityStatusSchema, type AdPlatform, type EntityStatus } from './enums.js';

/**
 * ANAHTAR KELİME VE ARAMA TERİMİ — okuma sözleşmesi (Faz 5).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `analytics.ts`in içinde değil, ayrı bir dosya
 * ═══════════════════════════════════════════════════════════════════════════
 * `analytics.ts` PLATFORMDAN BAĞIMSIZ: her satırı Meta'da da Google'da da
 * TikTok'ta da anlamlı. Anahtar kelime değil — Meta'da bu kavram YOK ve
 * hiçbir zaman olmayacak (`ConnectorCapabilities.canManageKeywords`
 * başlığı). Ortak dosyaya koymak, platforma özgü bir kavramı ortakmış gibi
 * göstermek olurdu; ayrı dosya arayüze de "bu ekran her hesapta yok" diyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Metrikler NEREDEN geliyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Kelime metrikleri `metrics_daily`de `level = 'KEYWORD'` satırları olarak
 * duruyor ve `entityId` bizim `Keyword.id`'miz. Yani kelime tablosu, kampanya
 * tablosuyla AYNI metrik SQL'ini kullanıyor — ikinci bir toplama yazılmadı.
 * Gerekçe `analytics-query.service.ts` başlığında: motorun okuduğu sayı ile
 * kullanıcının gördüğü sayı farklıysa "bu kural neden tetiklendi" sorusunun
 * cevabı yok demektir.
 *
 * Arama terimleri AYRI bir tabloda (`search_terms`) çünkü boyutları bir
 * VARLIK değil bir METİN: `entityId`si yok, olamaz da.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Anahtar kelime listesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sıralanabilir kolonlar.
 *
 * `text` / `status` / `cpcBid` varlık kolonları, gerisi metrik. İkisi ayrı
 * SQL ifadesine çevriliyor ve liste burada DARALTILIYOR ki `ORDER BY`a
 * kullanıcıdan gelen serbest metin asla ulaşmasın.
 */
export const KEYWORD_SORT_FIELDS = [
  'spend',
  'impressions',
  'clicks',
  'conversions',
  'revenue',
  'ctr',
  'cpc',
  'cpm',
  'cpa',
  'roas',
  'text',
  'status',
  'cpcBid',
] as const;
export type KeywordSort = (typeof KEYWORD_SORT_FIELDS)[number];

export const keywordListQuerySchema = metricFilterSchema.extend({
  sort: z.enum(KEYWORD_SORT_FIELDS).default('spend'),
  dir: sortDirSchema,
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  /**
   * Negatif kelimeleri de göster.
   *
   * 🔴 Varsayılan `false` ve bu bir tercih değil bir doğruluk kararı:
   * pozitif ve negatif kelimeler AYNI tabloda ve yalnız bu bayrakla
   * ayrılıyorlar. Filtresiz bir "en çok harcayan kelime" listesi, hiç
   * harcamamış negatifleri de içerir ve tablonun altı sıfırlarla dolar.
   * Negatifleri görmek AYRI bir iş (dışlama listesi yönetimi) ve ayrı bir
   * niyet.
   */
  includeNegative: boolParam.optional(),
  /// Yalnız negatifler — dışlama listesi ekranı. `includeNegative` ile
  /// birlikte gönderilirse bu kazanır: daha dar olan filtre daha spesifik
  /// bir niyet.
  onlyNegative: boolParam.optional(),
  /// `EXACT`, `PHRASE`, `BROAD`… Platform dizesi olduğu gibi karşılaştırılıyor
  /// (şema kararı: eşleşme türü normalize EDİLMİYOR).
  matchTypes: csvParam.pipe(z.array(z.string().max(40)).max(10)).optional(),
  /// Görünecek metrik kolonları. Boşsa varsayılan küme.
  columns: csvParam.pipe(z.array(metricKeySchema).max(10)).optional(),
});
export type KeywordListQuery = z.infer<typeof keywordListQuerySchema>;

export interface KeywordListRow {
  id: string;
  text: string;
  /// Platform dizesi (`EXACT` / `PHRASE` / `BROAD`). Normalize edilmedi.
  matchType: string;
  negative: boolean;
  status: EntityStatus;
  /**
   * Kelimenin KENDİ teklifi, hesabın para biriminde ANA birim.
   *
   * `null` = kampanya otomatik teklif kullanıyor. Arayüz burada `—`
   * göstermeli, `0` DEĞİL: sıfır teklif "hiç teklif verme" demek olurdu ve
   * kullanıcı teklifi kendisinin sıfırladığını sanardı.
   */
  cpcBid: number | null;
  adSetId: string;
  adSetName: string;
  campaignId: string;
  campaignName: string;
  adAccountId: string;
  accountName: string;
  currency: string;
  platform: AdPlatform;
  metrics: Partial<Record<MetricKey, number | null>>;
}

export interface KeywordListResponse extends Page<KeywordListRow> {
  /// Görünen SAYFANIN değil FİLTRENİN tamamının toplamı — tablo altındaki
  /// toplam satırı sayfa çevirince değişmemeli.
  totals: Partial<Record<MetricKey, number | null>>;
  currency: string | null;
  mixedCurrency: boolean;
  /**
   * 🔴 KELİMESİZ PLATFORM ≠ KELİMESİ OLMAYAN HESAP.
   *
   * `false` = kapsamdaki hesapların HİÇBİRİ arama ağı platformunda değil.
   * Arayüz bu durumda "henüz kelime yok" DEMEMELİ — o cümle kullanıcıyı
   * Google arayüzünde olmayan kelimeleri aramaya gönderir. Doğru cümle
   * "bu hesapta anahtar kelime diye bir kavram yok" (`i18n`
   * `keywords.platformUnsupported`).
   */
  platformSupported: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Arama terimi listesi
// ═══════════════════════════════════════════════════════════════════════════

export const SEARCH_TERM_SORT_FIELDS = [
  'spend',
  'impressions',
  'clicks',
  'conversions',
  'ctr',
  'cpc',
  'cpa',
  'text',
] as const;
export type SearchTermSort = (typeof SEARCH_TERM_SORT_FIELDS)[number];

export const searchTermListQuerySchema = metricFilterSchema.extend({
  sort: z.enum(SEARCH_TERM_SORT_FIELDS).default('spend'),
  dir: sortDirSchema,
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  /**
   * "Kötü performanslı arama terimi" ekranının iki eşiği.
   *
   * Eşikler SUNUCUDA çünkü ekranın sorusu "hangi terime boşuna para
   * veriyorum": tek gösterim almış bir terimi listeye koymak, gerçek
   * israfı gürültünün altında bırakır. Varsayılan YOK — eşik koymayı
   * arayüz seçiyor ve seçtiği değer URL'de görünüyor.
   */
  minImpressions: z.coerce.number().int().min(0).optional(),
  minClicks: z.coerce.number().int().min(0).optional(),
  /// Dönüşümü sıfır olanlar — "tıklıyor ama satın almıyor".
  zeroConversions: boolParam.optional(),
  /// Terimin zaten bir negatif kelimeyle dışlanıp dışlanmadığı SORULMUYOR:
  /// dışlanan terim rapora zaten düşmüyor. Bunun yerine "eşleşen kelimesi
  /// bilinen/bilinmeyen" ayrımı var — yakın varyantlar `keywordId` taşımıyor.
  onlyUnmatched: boolParam.optional(),
});
export type SearchTermListQuery = z.infer<typeof searchTermListQuerySchema>;

export interface SearchTermListRow {
  id: string;
  text: string;
  /// Hangi kelimeyle eşleşti. `null` = platform söylemedi (yakın varyant).
  /// Bu satırlar tam olarak kullanıcının görmek istediği satırlar: harcama
  /// yapıyor, kelime listesinde görünmüyor.
  keywordId: string | null;
  keywordText: string | null;
  adSetId: string;
  adSetName: string;
  campaignId: string;
  campaignName: string;
  adAccountId: string;
  accountName: string;
  currency: string;
  platform: AdPlatform;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  /// Türetilmişler — payda sıfırsa `null`, `0` DEĞİL. "Gösterim yok" ile
  /// "tıklama oranı sıfır" farklı şeyler.
  ctr: number | null;
  cpc: number | null;
  cpa: number | null;
}

export interface SearchTermListResponse extends Page<SearchTermListRow> {
  /// Filtrenin tamamının toplamı — sayfa değil.
  totals: {
    impressions: number;
    clicks: number;
    spend: number;
    conversions: number;
  };
  currency: string | null;
  mixedCurrency: boolean;
  /// Bkz. `KeywordListResponse.platformSupported` — aynı ayrım.
  platformSupported: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ⚠️ `PLATFORM_HAS_NO_KEYWORDS` BURADA YOK — `IntegrationErrorCode` içinde.
 * İki yerde tanımlamak, arayüzün hangi sabiti karşılaştıracağını belirsiz
 * bırakırdı ve barrel'da isim çakışması derlemeyi kırardı (ki bu iyi olurdu).
 */
export const KeywordErrorCode = {
  KEYWORD_NOT_FOUND: 'KEYWORD_NOT_FOUND',
  /// Kelime seviyesi filtre gönderildi ama kapsamdaki hiçbir hesap arama
  /// ağı platformunda değil. Boş liste dönmek "kelimen yok" derdi; bu kod
  /// "bu hesapta böyle bir kavram yok" diyor.
  NO_SEARCH_PLATFORM_CONNECTED: 'NO_SEARCH_PLATFORM_CONNECTED',
} as const;
export type KeywordErrorCode = (typeof KeywordErrorCode)[keyof typeof KeywordErrorCode];

/// Kelime ekranlarının çalıştığı platformlar. Tek yerde: arayüz "bu hesapta
/// kelime yok" mesajını bu listeye bakarak gösteriyor, koda gömülü bir
/// `=== 'GOOGLE'` karşılaştırmasıyla değil.
export const KEYWORD_CAPABLE_PLATFORMS: readonly AdPlatform[] = ['GOOGLE'] as AdPlatform[];

/// Şema tarafında `status` filtresi `metricFilterSchema`dan geliyor; burada
/// yalnız tipin dışa açık kalması için yeniden dışa veriliyor.
export const keywordStatusSchema = entityStatusSchema;
